import React, { memo, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import API from "../services/api";
import TagEditor from "../components/tags/TagEditor";
import ArchivedTags from "../components/tags/ArchivedTags";
import TagCreationForm from "../components/tags/TagCreationForm";
import GroupManagement from "../components/tags/GroupManagement";
import AddServer from "./AddServer";
import OpcNodeDetails from "../components/tags/OpcNodeDetails";
import RegisteredTags from "../components/tags/RegisteredTags";
import "./AddTag.css";

const THEME_STORAGE_KEY = "plcGatewayHomeTheme";

const readPageTheme = () => {
    try {
        const saved = localStorage.getItem(THEME_STORAGE_KEY);
        if (saved === "light" || saved === "dark") {
            return saved;
        }
    } catch (error) {
        console.error("Error reading theme preference:", error);
    }

    if (window.matchMedia && window.matchMedia("(prefers-color-scheme: dark)").matches) {
        return "dark";
    }

    return "light";
};

const messageTone = (text) => {
    const value = String(text || "").toLowerCase();
    if (value.includes("fail") || value.includes("please") || value.includes("unable")) {
        return "error";
    }
    if (value.includes("fetching") || value.includes("already")) {
        return "pending";
    }
    if (value.includes("success") || value.includes("copied")) {
        return "success";
    }
    return "info";
};

const buildOpcTree = (rows) => {
    const items = rows.map((row, index) => ({
        ...row,
        key: `${row.node_id || "node"}-${index}`,
        children: [],
    }));
    const byDisplayName = new Map();

    items.forEach((item) => {
        const label = item.display_name || "";
        const matches = byDisplayName.get(label) || [];
        matches.push(item);
        byDisplayName.set(label, matches);
    });

    const roots = [];
    items.forEach((item) => {
        const parentName = item.parents;
        const candidates = parentName && parentName !== "objects"
            ? (byDisplayName.get(parentName) || []).filter((candidate) => candidate !== item)
            : [];

        if (candidates.length === 1) {
            candidates[0].children.push(item);
        } else {
            roots.push(item);
        }
    });

    return { items, roots };
};

const filterOpcTree = (nodes, query) => {
    if (!query) {
        return nodes;
    }

    return nodes.reduce((kept, node) => {
        const children = filterOpcTree(node.children, query);
        const haystack = `${node.node_id || ""} ${node.display_name || ""} ${node.parents || ""} ${node.type || ""}`.toLowerCase();
        if (haystack.includes(query) || children.length > 0) {
            kept.push({ ...node, children });
        }
        return kept;
    }, []);
};

const isSameRegistration = (tag, serverId, nodeId) => {
    if (!tag || serverId === "" || serverId === null || serverId === undefined || !nodeId) {
        return false;
    }
    return String(tag.server) === String(serverId) && String(tag.node_id) === String(nodeId);
};

const OpcTreeItem = memo(function OpcTreeItem({ node, depth, selectedKey, onSelect, forceOpen, expansion }) {
    const [open, setOpen] = useState(depth < 1);
    const hasChildren = node.children.length > 0;

    useEffect(() => {
        if (forceOpen) {
            setOpen(true);
        }
    }, [forceOpen]);

    useEffect(() => {
        if (!expansion) {
            return;
        }
        setOpen(expansion.mode === "open");
    }, [expansion]);

    return (
        <li
            className="tag-page__tree-item"
            role="treeitem"
            aria-selected={selectedKey === node.key}
            aria-expanded={hasChildren ? open : undefined}
        >
            <div className="tag-page__tree-row" style={{ "--tree-depth": depth }}>
                {hasChildren ? (
                    <button
                        type="button"
                        className="tag-page__tree-toggle"
                        aria-label={open ? `Collapse ${node.display_name || "node"}` : `Expand ${node.display_name || "node"}`}
                        onClick={() => setOpen((current) => !current)}
                    >
                        {open ? "▾" : "▸"}
                    </button>
                ) : (
                    <span className="tag-page__tree-toggle" aria-hidden="true" />
                )}
                <button
                    type="button"
                    className={`tag-page__tree-label${selectedKey === node.key ? " is-selected" : ""}`}
                    aria-pressed={selectedKey === node.key}
                    onClick={() => onSelect(node)}
                >
                    <span>{node.display_name || node.node_id}</span>
                    <span className="tag-page__tree-type">{node.type || "Unknown"}</span>
                </button>
            </div>
            {hasChildren && open && (
                <ul className="tag-page__tree" role="group">
                    {node.children.map((child) => (
                        <OpcTreeItem
                            key={child.key}
                            node={child}
                            depth={depth + 1}
                            selectedKey={selectedKey}
                            onSelect={onSelect}
                            forceOpen={forceOpen}
                            expansion={expansion}
                        />
                    ))}
                </ul>
            )}
        </li>
    );
});

const AddTag = ({ access = null }) => {
    const theme = readPageTheme();
    const browseServerRef = useRef("");
    const editorRef = useRef(null);
    const [searchParams, setSearchParams] = useSearchParams();
    const [browseServerId, setBrowseServerId] = useState("");
    const [servers, setServers] = useState([]);
    const [groups, setGroups] = useState([]);
    const [tags, setTags] = useState([]);
    const [opcTags, setOpcTags] = useState([]);
    const [browseMessage, setBrowseMessage] = useState("");
    const [loadingOpcTags, setLoadingOpcTags] = useState(false);
    const [loadingLists, setLoadingLists] = useState(true);
    const [listError, setListError] = useState("");
    const [tagQuery, setTagQuery] = useState("");
    const [tagServerFilter, setTagServerFilter] = useState("all");
    const [opcQuery, setOpcQuery] = useState("");
    const [selectedOpcNode, setSelectedOpcNode] = useState(null);
    const [browsedServerId, setBrowsedServerId] = useState("");
    const [selectedStoredId, setSelectedStoredId] = useState(null);
    const [editorOpen, setEditorOpen] = useState(false);
    const [statusById, setStatusById] = useState({});
    const editorDirtyRef = useRef(false);
    const detailsRequestRef = useRef(0);
    const [tagReload, setTagReload] = useState(0);
    const [expansion, setExpansion] = useState(null);
    const [nodeDetails, setNodeDetails] = useState(null);
    const [detailsLoading, setDetailsLoading] = useState(false);
    const [detailsError, setDetailsError] = useState("");
    const requestedTab = searchParams.get("tab");
    const canManageGroups = !access || ["admin", "superadmin"].includes(access.role);
    const canViewServers = !access || ["admin", "superadmin"].includes(access.role);
    const canViewArchived = access?.role === "superadmin";
    const canCreateServers = canViewServers;
    const activeTab = requestedTab === "browser" || (requestedTab === "archived" && canViewArchived)
        || (requestedTab === "groups" && canManageGroups)
        || (requestedTab === "server" && canViewServers)
        ? requestedTab
        : "add";
    browseServerRef.current = browseServerId;

    useEffect(() => {
        let active = true;

        const loadLists = async () => {
            setLoadingLists(true);
            const errors = [];

            try {
                const response = await API.get(access ? "/access/tag-sources/" : "/servers/");
                if (active) {
                    setServers(response.data);
                }
            } catch (error) {
                console.error("Failed to fetch servers:", error);
                errors.push("Unable to load servers.");
            }

            try {
                const response = await API.get("/tags/");
                if (active) {
                    setTags(response.data);
                }
            } catch (error) {
                console.error("Failed to fetch tags:", error);
                errors.push("Unable to load tags.");
            }

            try {
                const response = await API.get("/latest-tag-values/");
                if (active) {
                    const nextStatus = {};
                    (Array.isArray(response.data) ? response.data : []).forEach((row) => {
                        if (row.tag_id != null) {
                            nextStatus[row.tag_id] = row.connection_status || "UNKNOWN";
                        }
                    });
                    setStatusById(nextStatus);
                }
            } catch (error) {
                console.error("Failed to fetch tag status:", error);
            }

            try {
                const response = await API.get("/tag-groups/");
                if (active) {
                    setGroups(Array.isArray(response.data) ? response.data : []);
                }
            } catch (error) {
                console.error("Failed to fetch tag groups:", error);
            }

            if (active) {
                setListError(errors.join(" "));
                setLoadingLists(false);
            }
        };

        loadLists();

        return () => {
            active = false;
        };
    }, [tagReload, access]);

    useEffect(() => {
        if (selectedStoredId == null || !Array.isArray(tags)) {
            return;
        }
        if (!tags.some((tag) => tag.id === selectedStoredId)) {
            editorDirtyRef.current = false;
            setSelectedStoredId(null);
            setEditorOpen(false);
        }
    }, [tags, selectedStoredId]);

    const serverNameById = useMemo(() => {
        const names = {};
        if (Array.isArray(servers)) {
            servers.forEach((server) => {
                names[String(server.id)] = server.name;
            });
        }
        return names;
    }, [servers]);

    const visibleTags = useMemo(() => {
        const rows = Array.isArray(tags) ? tags : [];
        const query = tagQuery.trim().toLowerCase();

        return rows.filter((tag) => {
            const matchesServer = tagServerFilter === "all" || String(tag.server) === tagServerFilter;
            if (!matchesServer) {
                return false;
            }
            if (!query) {
                return true;
            }
            return `${tag.name || ""} ${tag.node_id || ""}`.toLowerCase().includes(query);
        });
    }, [tags, tagQuery, tagServerFilter]);

    const opcTree = useMemo(
        () => buildOpcTree(Array.isArray(opcTags) ? opcTags : []),
        [opcTags]
    );
    const opcQueryText = opcQuery.trim().toLowerCase();
    const visibleOpcTree = useMemo(
        () => filterOpcTree(opcTree.roots, opcQueryText),
        [opcTree, opcQueryText]
    );

    const alreadyRegistered = useMemo(() => {
        if (!selectedOpcNode?.node_id || !browsedServerId || !Array.isArray(tags)) {
            return false;
        }
        return tags.some((tag) => isSameRegistration(tag, browsedServerId, selectedOpcNode.node_id));
    }, [tags, selectedOpcNode, browsedServerId]);

    const selectOpcNode = useCallback((node) => {
        setSelectedOpcNode(node);
        setNodeDetails(null);
        setDetailsError("");
        const server = browseServerRef.current;
        if (!node?.node_id || !server) {
            setDetailsLoading(false);
            return;
        }
        const requestId = detailsRequestRef.current + 1;
        detailsRequestRef.current = requestId;
        setDetailsLoading(true);
        API.post("/opc-node-details/", {
            server_id: server,
            node_id: node.node_id,
        }).then((response) => {
            if (detailsRequestRef.current !== requestId) {
                return;
            }
            setNodeDetails(response.data);
        }).catch((error) => {
            if (detailsRequestRef.current !== requestId) {
                return;
            }
            const detail = error?.response?.data?.detail || error?.response?.data?.error;
            setDetailsError(detail || "Failed to read node metadata.");
            console.error(error);
        }).finally(() => {
            if (detailsRequestRef.current === requestId) {
                setDetailsLoading(false);
            }
        });
    }, []);

    const handleBrowseServerChange = (value) => {
        setBrowseServerId(value);
        setOpcTags([]);
        setSelectedOpcNode(null);
        setBrowsedServerId("");
        setExpansion(null);
        setBrowseMessage("");
        setLoadingOpcTags(false);
        setNodeDetails(null);
        setDetailsError("");
        setDetailsLoading(false);
        detailsRequestRef.current += 1;
    };

    const handleFetchOpcTags = async () => {
        if (!browseServerId) {
            setBrowseMessage("Please select a server.");
            return;
        }

        const requestedServerId = browseServerId;
        setLoadingOpcTags(true);
        setBrowseMessage("Fetching OPC tags...");
        try {
            const response = await API.post("/browse-tags/", {
                server_id: requestedServerId,
            });
            if (browseServerRef.current !== requestedServerId) {
                return;
            }
            setOpcTags(response.data.tags);
            setBrowsedServerId(requestedServerId);
            setSelectedOpcNode(null);
            setExpansion(null);
            setNodeDetails(null);
            setDetailsError("");
            setDetailsLoading(false);
            detailsRequestRef.current += 1;
            setBrowseMessage("OPC tags fetched successfully.");
        } catch (error) {
            if (browseServerRef.current === requestedServerId) {
                const detail = error?.response?.data?.detail || error?.response?.data?.error;
                setBrowseMessage(detail || "Failed to fetch OPC tags.");
            }
            console.error(error);
        }
        if (browseServerRef.current === requestedServerId) {
            setLoadingOpcTags(false);
        }
    };

    const showTab = (next) => {
        const nextParams = new URLSearchParams(searchParams);
        if (next === "add") {
            nextParams.delete("tab");
        } else {
            nextParams.set("tab", next);
        }
        setSearchParams(nextParams, { replace: true });
    };

    const openStoredTag = (tagId) => {
        if (tagId !== selectedStoredId && editorDirtyRef.current) {
            const discard = window.confirm("This tag has unsaved changes. Discard them and open the selected tag?");
            if (!discard) {
                setEditorOpen(true);
                return;
            }
        }
        setSelectedStoredId(tagId);
        setEditorOpen(true);
    };

    const closeEditor = () => {
        setEditorOpen(false);
    };

    useEffect(() => {
        if (!editorOpen || !editorRef.current) {
            return;
        }
        editorRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
    }, [editorOpen, selectedStoredId]);

    const tagCount = Array.isArray(tags) ? tags.length : 0;
    const opcCount = Array.isArray(opcTags) ? opcTags.length : 0;
    const serverOptions = Array.isArray(servers) ? servers : [];
    const selectedStoredTag = Array.isArray(tags)
        ? tags.find((tag) => tag.id === selectedStoredId) || null
        : null;
    return (
        <div className="tag-page" data-theme={theme}>
            <header className="tag-page__top">
                <div>
                    <p className="tag-page__eyebrow">PLC configuration</p>
                    <h1>PLC Management</h1>
                    <p className="tag-page__subtitle">
                        Manage OPC UA servers, tags, groups, browsing, and archived devices in one place.
                    </p>
                </div>
            </header>

            {selectedStoredTag ? (
                <section
                    className="tag-page__card tag-page__manage"
                    id="tag-editor-panel"
                    ref={editorRef}
                    aria-labelledby="tag-editor-heading"
                    hidden={!editorOpen}
                >
                    <div className="tag-page__manage-head">
                        <div>
                            <h2 id="tag-editor-heading">Tag configuration</h2>
                            <p>Rename, recording, formula, and group settings for the selected tag.</p>
                        </div>
                        <button
                            type="button"
                            className="tag-page__editor-close"
                            aria-label="Close tag editor"
                            onClick={closeEditor}
                        >
                            ×
                        </button>
                    </div>
                    <TagEditor
                        tagId={selectedStoredTag.id}
                        serverName={serverNameById[String(selectedStoredTag.server)]}
                        onChanged={() => setTagReload((value) => value + 1)}
                        onDirtyChange={(dirty) => {
                            editorDirtyRef.current = dirty;
                        }}
                    />
                </section>
            ) : null}

            <div className="tag-page__tabs" role="tablist" aria-label="Tags">
                {canViewServers ? (
                    <button
                        type="button"
                        role="tab"
                        id="tags-tab-server"
                        aria-selected={activeTab === "server"}
                        aria-controls="tags-panel-server"
                        onClick={() => showTab("server")}
                    >
                        Add Server
                    </button>
                ) : null}
                <button
                    type="button"
                    role="tab"
                    id="tags-tab-add"
                    aria-selected={activeTab === "add"}
                    aria-controls="tags-panel-add"
                    onClick={() => showTab("add")}
                >
                    Add Tag
                </button>
                <button
                    type="button"
                    role="tab"
                    id="tags-tab-browser"
                    aria-selected={activeTab === "browser"}
                    aria-controls="tags-panel-browser"
                    onClick={() => showTab("browser")}
                >
                    OPC UA Browser
                </button>
                {canManageGroups ? (
                    <button
                        type="button"
                        role="tab"
                        id="tags-tab-groups"
                        aria-selected={activeTab === "groups"}
                        aria-controls="tags-panel-groups"
                        onClick={() => showTab("groups")}
                    >
                        Group Management
                    </button>
                ) : null}
                {canViewArchived ? <button
                    type="button"
                    role="tab"
                    id="tags-tab-archived"
                    aria-selected={activeTab === "archived"}
                    aria-controls="tags-panel-archived"
                    onClick={() => showTab("archived")}
                >
                    Archived Tags
                </button> : null}
            </div>

            {canViewServers ? (
                <section
                    className="tag-page__card"
                    id="tags-panel-server"
                    role="tabpanel"
                    aria-labelledby="tags-tab-server"
                    hidden={activeTab !== "server"}
                >
                    <div className="tag-page__card-head">
                        <h2>Server management</h2>
                        <p>Add an OPC UA endpoint, test its connection, and review saved servers.</p>
                    </div>
                    {activeTab === "server" ? <AddServer embedded canCreate={canCreateServers} isSuperadmin={canViewArchived} /> : null}
                </section>
            ) : null}

            <section className="tag-page__card" id="tags-panel-add" role="tabpanel" aria-labelledby="tags-tab-add" hidden={activeTab !== "add"}>
                <div className="tag-page__card-head">
                    <h2 id="create-tag-heading">Create tag</h2>
                    <p>The tag is saved only when you click Add Tag. Required fields are marked.</p>
                </div>
                <TagCreationForm
                    idPrefix="manual"
                    mode="manual"
                    servers={serverOptions}
                    groups={groups}
                    submitLabel="Add Tag"
                    onCreated={() => setTagReload((value) => value + 1)}
                />
            </section>

            {canManageGroups ? (
                <section
                    className="tag-page__card"
                    id="tags-panel-groups"
                    role="tabpanel"
                    aria-labelledby="tags-tab-groups"
                    hidden={activeTab !== "groups"}
                >
                    <div className="tag-page__card-head">
                        <h2>Group management</h2>
                        <p>Create groups, choose a parent, edit group details, and review the current hierarchy.</p>
                    </div>
                    {activeTab === "groups" ? (
                        <GroupManagement onChanged={() => setTagReload((value) => value + 1)} />
                    ) : null}
                </section>
            ) : null}

            <section
                className="tag-page__card"
                id="tags-panel-browser"
                role="tabpanel"
                aria-labelledby="tags-tab-browser"
                hidden={activeTab !== "browser"}
            >
                <div className="tag-page__card-head">
                    <h2 id="opc-tags-heading">OPC UA browser</h2>
                    <p>Browse runs only from Fetch OPC Tags. Add Tag to System saves the selected node from this tab.</p>
                </div>

                <div className="tag-page__toolbar">
                    <div className="tag-page__field">
                        <label htmlFor="browse-server">OPC Server</label>
                        <select
                            id="browse-server"
                            value={browseServerId}
                            onChange={(e) => handleBrowseServerChange(e.target.value)}
                        >
                            <option value="">Select a server</option>
                            {serverOptions.map((server) => (
                                <option key={server.id} value={server.id}>
                                    {server.name}
                                </option>
                            ))}
                        </select>
                    </div>
                    <button
                        type="button"
                        className="tag-page__button tag-page__button--primary"
                        onClick={handleFetchOpcTags}
                        disabled={loadingOpcTags}
                    >
                        {loadingOpcTags ? "Fetching..." : "Fetch OPC Tags"}
                    </button>
                    <div className="tag-page__field tag-page__field--search">
                        <label htmlFor="opc-search">Search</label>
                        <input
                            id="opc-search"
                            type="search"
                            value={opcQuery}
                            onChange={(e) => setOpcQuery(e.target.value)}
                            placeholder="Node, name, parent, or type"
                            disabled={opcCount === 0}
                        />
                    </div>
                    <button
                        type="button"
                        className="tag-page__button"
                        onClick={() => setExpansion((current) => ({ mode: "open", id: (current?.id || 0) + 1 }))}
                        disabled={opcCount === 0}
                    >
                        Expand All
                    </button>
                    <button
                        type="button"
                        className="tag-page__button"
                        onClick={() => setExpansion((current) => ({ mode: "closed", id: (current?.id || 0) + 1 }))}
                        disabled={opcCount === 0}
                    >
                        Collapse All
                    </button>
                </div>

                {browseMessage && (
                    <p className={`tag-page__banner is-${messageTone(browseMessage)}`} role="status">
                        {browseMessage}
                    </p>
                )}

                {loadingOpcTags ? (
                    <p className="tag-page__empty">Fetching OPC tags…</p>
                ) : opcCount === 0 ? (
                    <p className="tag-page__empty">No browse results yet. Choose a browse server, then click Fetch OPC Tags.</p>
                ) : visibleOpcTree.length === 0 ? (
                    <p className="tag-page__empty">No browse results match this search.</p>
                ) : (
                    <div className="tag-page__browser">
                        <div className="tag-page__tree-pane">
                            <h3>OPC UA tree</h3>
                            <ul className="tag-page__tree" role="tree" aria-label="OPC UA nodes">
                                {visibleOpcTree.map((node) => (
                                    <OpcTreeItem
                                        key={node.key}
                                        node={node}
                                        depth={0}
                                        selectedKey={selectedOpcNode?.key}
                                        onSelect={selectOpcNode}
                                        forceOpen={Boolean(opcQueryText)}
                                        expansion={expansion}
                                    />
                                ))}
                            </ul>
                        </div>
                        <div className="tag-page__details" aria-live="polite">
                            <h3>Node details</h3>
                            {selectedOpcNode ? (
                                <>
                                    <OpcNodeDetails
                                        details={nodeDetails}
                                        loading={detailsLoading}
                                        error={detailsError}
                                        listing={selectedOpcNode}
                                    />
                                    {detailsError ? null : detailsLoading ? (
                                        <p className="tag-page__empty">Reading node metadata before the tag form is shown.</p>
                                    ) : (
                                        <TagCreationForm
                                            idPrefix="browser"
                                            mode="browser"
                                            servers={serverOptions}
                                            groups={groups}
                                            listing={selectedOpcNode}
                                            details={nodeDetails}
                                            serverId={browsedServerId}
                                            alreadyRegistered={alreadyRegistered}
                                            submitLabel="Add Tag to System"
                                            onCreated={() => {
                                                setBrowseMessage("Tag added successfully.");
                                                setTagReload((value) => value + 1);
                                            }}
                                        />
                                    )}
                                </>
                            ) : (
                                <p className="tag-page__empty">Select a node to see its details.</p>
                            )}
                        </div>
                    </div>
                )}
            </section>

            {canViewArchived ? <section
                className="tag-page__card"
                id="tags-panel-archived"
                role="tabpanel"
                aria-labelledby="tags-tab-archived"
                hidden={activeTab !== "archived"}
            >
                <div className="tag-page__card-head">
                    <h2 id="archived-tags-heading">Archived tags</h2>
                    <p>These tags are out of service. Archived is separate from online and offline.</p>
                </div>
                <ArchivedTags
                    servers={serverOptions}
                    serverNameById={serverNameById}
                    onChanged={() => setTagReload((value) => value + 1)}
                />
            </section> : null}

            <div hidden={activeTab === "archived" || activeTab === "groups" || activeTab === "server"}>
            <RegisteredTags
                tags={visibleTags}
                totalCount={tagCount}
                loading={loadingLists}
                error={listError}
                query={tagQuery}
                onQueryChange={setTagQuery}
                serverFilter={tagServerFilter}
                onServerFilterChange={setTagServerFilter}
                servers={serverOptions}
                serverNameById={serverNameById}
                statusById={statusById}
                selectedId={selectedStoredId}
                onEdit={openStoredTag}
            />
            </div>
        </div>
    );
};

export default AddTag;
